import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, Loader2 } from 'lucide-react';
import { admissionsService } from '../../../firebase/admissions';
import { useRealtime } from '../../../contexts/RealtimeContext';
import toast from 'react-hot-toast';

const CurriculumManagement = () => {
  const { userProfile } = useRealtime();
  const [curriculum, setCurriculum] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    duration: '',
    fees: '',
    subjects: '',
    eligibility: '',
    highlights: ''
  });

  useEffect(() => {
    loadCurriculum();
  }, []);

  const loadCurriculum = async () => {
    try {
      setLoading(true);
      const institutionId = userProfile?.coachingId || userProfile?.institutionId;
      if (!institutionId) {
        setLoading(false);
        return;
      }

      const result = await admissionsService.getInstitutionCurriculum(institutionId);
      if (result.success) {
        setCurriculum(result.curriculum || []);
      } else {
        toast.error('Failed to load curriculum');
      }
    } catch (error) {
      toast.error('Error loading curriculum');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleEdit = (item) => {
    setFormData({
      title: item.title || '',
      description: item.description || '',
      duration: item.duration || '',
      fees: item.fees || '',
      subjects: item.subjects || '',
      eligibility: item.eligibility || '',
      highlights: item.highlights || ''
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleCancel = () => {
    setFormData({
      title: '',
      description: '',
      duration: '',
      fees: '',
      subjects: '',
      eligibility: '',
      highlights: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    try {
      setSaving(true);
      const institutionId = userProfile?.coachingId || userProfile?.institutionId;
      
      const result = editingId
        ? await admissionsService.saveCurriculumItem({
            ...formData,
            institutionId
          }, editingId)
        : await admissionsService.saveCurriculumItem({
            ...formData,
            institutionId
          });

      if (result.success) {
        toast.success(editingId ? 'Curriculum updated successfully' : 'Curriculum added successfully');
        handleCancel();
        loadCurriculum();
      } else {
        toast.error('Failed to save: ' + result.error);
      }
    } catch (error) {
      toast.error('Error saving curriculum: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this curriculum item?')) {
      return;
    }

    try {
      const result = await admissionsService.deleteCurriculumItem(id);
      if (result.success) {
        toast.success('Curriculum deleted successfully');
        loadCurriculum();
      } else {
        toast.error('Failed to delete: ' + result.error);
      }
    } catch (error) {
      toast.error('Error deleting curriculum: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Curriculum Management</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Manage courses and programs offered by your institution
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Add Course
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {editingId ? 'Edit Course' : 'Add New Course'}
            </h2>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Course Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="e.g., JEE Main Preparation, NEET Coaching"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                rows={4}
                placeholder="Describe the course..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Duration
                </label>
                <input
                  type="text"
                  value={formData.duration}
                  onChange={(e) => handleInputChange('duration', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., 1 Year, 6 Months"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Fees
                </label>
                <input
                  type="text"
                  value={formData.fees}
                  onChange={(e) => handleInputChange('fees', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., ₹50,000/year"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Subjects
              </label>
              <input
                type="text"
                value={formData.subjects}
                onChange={(e) => handleInputChange('subjects', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="e.g., Physics, Chemistry, Mathematics"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Eligibility
              </label>
              <input
                type="text"
                value={formData.eligibility}
                onChange={(e) => handleInputChange('eligibility', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="e.g., Class 11-12, 10+2 Pass"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Highlights
              </label>
              <textarea
                value={formData.highlights}
                onChange={(e) => handleInputChange('highlights', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                rows={3}
                placeholder="Key features and highlights (one per line)"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Curriculum List */}
      {curriculum.length === 0 && !showForm ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-12 text-center">
          <p className="text-gray-600 dark:text-gray-400">No curriculum items yet. Add your first course!</p>
        </div>
      ) : (
        !showForm && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {curriculum.map((item) => (
              <div key={item.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {item.title}
                  </h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(item)}
                      className="text-blue-600 hover:text-blue-800"
                      title="Edit"
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="text-red-600 hover:text-red-800"
                      title="Delete"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {item.description && (
                  <p className="text-gray-600 dark:text-gray-400 mb-3">{item.description}</p>
                )}

                <div className="space-y-2 text-sm">
                  {item.duration && (
                    <p><span className="font-medium text-gray-700 dark:text-gray-300">Duration:</span> {item.duration}</p>
                  )}
                  {item.fees && (
                    <p><span className="font-medium text-gray-700 dark:text-gray-300">Fees:</span> {item.fees}</p>
                  )}
                  {item.subjects && (
                    <p><span className="font-medium text-gray-700 dark:text-gray-300">Subjects:</span> {item.subjects}</p>
                  )}
                  {item.eligibility && (
                    <p><span className="font-medium text-gray-700 dark:text-gray-300">Eligibility:</span> {item.eligibility}</p>
                  )}
                  {item.highlights && (
                    <div>
                      <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">Highlights:</p>
                      <p className="text-gray-600 dark:text-gray-400 whitespace-pre-line">{item.highlights}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
};

export default CurriculumManagement;
